import { useForm } from "@inertiajs/react";
import type { FormEvent, TextareaHTMLAttributes } from "react";

import Button from "@/components/Button";
import LinkButton from "@/components/LinkButton";
import { useGenderedText } from "@/lib/genderedText";
import { closeCourse, courses } from "@/routes";
import type { Course, CourseForm } from "@/types";

type CourseFormFields = {
  food_comment: string;
  cleaning_comment: string;
  administration_comment: string;
  house_comment: string;
  general_comment: string;
};

const QUESTIONS: { key: keyof CourseFormFields; label: string }[] = [
  {
    key: "food_comment",
    label: "¿Tienes algún comentario respecto a la Comida?",
  },
  {
    key: "cleaning_comment",
    label: "¿Tienes algún comentario respecto al Aseo?",
  },
  {
    key: "administration_comment",
    label: "¿Tienes algún comentario respecto a la Administración?",
  },
  {
    key: "house_comment",
    label: "¿Tienes algún comentario respecto a la Casa en General?",
  },
  {
    key: "general_comment",
    label: "Si tienes algún comentario o pregunta, escríbelo a continuación",
  },
];

function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className="w-full resize-y rounded-xl border-2 border-ink/20 p-3 font-sans text-sm text-ink placeholder:text-ink/40 focus:border-steel focus:outline-none"
      rows={3}
      {...props}
    />
  );
}

type ClosingFile = { filename: string; url: string };

type Props = {
  data: Course;
  course_form: CourseForm | null;
  closing_file: ClosingFile | null;
};

export default function CoursesClose({
  data: course,
  course_form,
  closing_file,
}: Props) {
  const t = useGenderedText();
  // The backend only requires these comments the first time the course is
  // closed (CourseForm validates presence on: :create) — once a CourseForm
  // already exists, editing it on a later close is optional.
  const commentsRequired = course_form === null;

  const form = useForm<CourseFormFields & { closing_file: File | null }>({
    food_comment: course_form?.food_comment ?? "",
    cleaning_comment: course_form?.cleaning_comment ?? "",
    administration_comment: course_form?.administration_comment ?? "",
    house_comment: course_form?.house_comment ?? "",
    general_comment: course_form?.general_comment ?? "",
    closing_file: null,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    form.transform(({ closing_file, ...data }) => ({
      course_form: data,
      ...(closing_file ? { closing_file } : {}),
    }));
    form.post(closeCourse(course.id).url, {
      forceFormData: !!form.data.closing_file,
    });
  }

  const closingFileName =
    form.data.closing_file?.name ?? closing_file?.filename ?? null;

  return (
    <div className="mx-auto w-[90%] max-w-2xl py-10">
      <h1 className="mb-2 font-sans text-3xl font-semibold text-ink">
        Cerrar {course.name || "curso"}
      </h1>
      <p className="mb-6 font-sans text-sm text-ink">
        {t("cerrarCursoMensaje")}
      </p>

      <form onSubmit={submit} className="flex flex-col gap-4">
        {QUESTIONS.map(({ key, label }) => (
          <label key={key} htmlFor={key} className="flex flex-col gap-1">
            <span className="font-sans text-sm text-ink">
              {label}
              {commentsRequired && <span className="text-deep-red"> *</span>}
            </span>
            <Textarea
              id={key}
              value={form.data[key]}
              onChange={(e) => form.setData(key, e.target.value)}
              required={commentsRequired}
            />
          </label>
        ))}

        {!course.is_public && (
          <div className="flex flex-col gap-2">
            <span className="font-sans text-sm text-ink">
              Planilla Excel (opcional)
            </span>
            {closingFileName && (
              <span className="font-sans text-sm text-ink/70">
                {closingFileName}
              </span>
            )}
            <label className="w-fit cursor-pointer rounded-full border-2 border-ink px-4 py-2 font-sans text-sm text-ink hover:border-steel">
              {closingFileName ? "Cambiar archivo" : "Subir archivo"}
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) =>
                  form.setData("closing_file", e.target.files?.[0] ?? null)
                }
              />
            </label>
          </div>
        )}

        <div className="mt-2 flex gap-3">
          <Button type="submit" variant="danger" disabled={form.processing}>
            Cerrar curso
          </Button>
          <LinkButton href={courses.show(course.id).url}>Cancelar</LinkButton>
        </div>
      </form>
    </div>
  );
}
